import { useState } from 'react';
import { errorMessage } from '@/lib/api/client';
import { bookCentreClass, bookCounselling } from '@/api/marketing.api';
import { Modal } from '@/components/common/Modal';
import { useAuth } from '@/features/auth/AuthContext';

/**
 * 1:1 counselling booking — openCounsel() (:5167) + bookCounsel() (:5171),
 * markup from :5315.
 *
 * Doubles as the centre free-class booking: bookFreeClass (:5209) does not
 * book anything itself, it presets goal="Free trial class" and
 * slot="Weekend morning" and opens this same modal. `preset.centre*` carries
 * the centre through, which the prototype drops — see the note on the endpoint.
 */

export const COUNSEL_GOALS = [
  'Choosing the right course',
  'Career change advice',
  'Free trial class',
  'Corporate / team training',
  'Something else',
];

export const COUNSEL_SLOTS = [
  'Weekday morning',
  'Weekday afternoon',
  'Weekday evening',
  'Weekend morning',
  'Weekend evening',
];

export interface CounselPreset {
  goal?: string;
  slot?: string;
  centreSlug?: string;
  centreName?: string;
}

export function CounselModal({
  preset,
  onClose,
  onBooked,
}: {
  preset?: CounselPreset;
  onClose: () => void;
  onBooked: (msg: string) => void;
}) {
  const { user } = useAuth();
  // openCounsel (:5167) pre-fills name/email for a logged-in user.
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(user?.email ?? '');
  const [goal, setGoal] = useState(preset?.goal ?? COUNSEL_GOALS[0]!);
  const [slot, setSlot] = useState(preset?.slot ?? COUNSEL_SLOTS[0]!);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const forCentre = !!preset?.centreSlug;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // bookCounsel (:5176) — "Name and phone are required"
    if (!name.trim() || !phone.trim()) {
      setError('Name and phone are required');
      return;
    }
    setBusy(true);
    try {
      if (forCentre) {
        await bookCentreClass(preset!.centreSlug!, { name, phone, email: email || undefined, slot });
        onBooked(`Free class booked at ${preset!.centreName} — our advisor will call you`);
      } else {
        await bookCounselling({ name, phone, email: email || undefined, goal, slot });
        onBooked('Booked — our advisor will call you');
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} label="Book a free 1:1 session">
      <h2 className="text-[19px]">
        {forCentre ? `Book a free class at ${preset!.centreName}` : 'Book a free 1:1 session'}
      </h2>
      <p className="mb-5 text-sm text-[var(--ink-soft)]">
        {forCentre
          ? 'Tell us how to reach you and an advisor will confirm your seat.'
          : 'Talk to an advisor about which course fits your goal. Free, no obligation.'}
      </p>

      <form onSubmit={(e) => void submit(e)}>
        <div className="mb-3.5">
          <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="cnName">Your name</label>
          <input className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="cnName" data-testid="cn-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="mb-3.5">
          <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="cnPhone">Phone</label>
          <input className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="cnPhone" data-testid="cn-phone" placeholder="01X-XXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="mb-3.5">
          <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="cnEmail">Email (optional)</label>
          <input
            id="cnEmail"
            data-testid="cn-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]"
          />
        </div>

        {/* A centre booking's goal IS the free trial class — asking again would
            let the two disagree. */}
        {!forCentre ? (
          <div className="mb-3.5">
            <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="cnGoal">What do you want help with?</label>
            <select className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="cnGoal" data-testid="cn-goal" value={goal} onChange={(e) => setGoal(e.target.value)}>
              {COUNSEL_GOALS.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="mb-3.5">
          <label className="mb-1.5 block text-[13.5px] font-semibold" htmlFor="cnSlot">Preferred time</label>
          <select className="w-full rounded-[10px] border-[1.5px] border-[var(--line)] px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--cobalt)]" id="cnSlot" data-testid="cn-slot" value={slot} onChange={(e) => setSlot(e.target.value)}>
            {COUNSEL_SLOTS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>

        {error ? (
          <p data-testid="cn-error" className="text-[13.5px] text-[var(--coral)]">
            {error}
          </p>
        ) : null}

        <button className="inline-flex w-full items-center justify-center rounded-full bg-linear-to-br from-[var(--ember)] to-[var(--cobalt)] px-6 py-3 text-[15px] font-semibold text-white shadow-[var(--shadow-orange)] disabled:opacity-60" type="submit" data-testid="cn-submit" disabled={busy}>
          {busy ? 'Booking…' : forCentre ? 'Book my free class' : 'Book my session'}
        </button>
      </form>
    </Modal>
  );
}
